import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Sparkles } from "lucide-react";

const SHAPES = [
  { name: "Circle", svg: <circle cx="30" cy="30" r="22" /> },
  { name: "Square", svg: <rect x="8" y="8" width="44" height="44" rx="4" /> },
  { name: "Triangle", svg: <polygon points="30,8 54,52 6,52" /> },
  { name: "Star", svg: <polygon points="30,6 36,22 54,22 40,32 46,50 30,40 14,50 20,32 6,22 24,22" /> },
  { name: "Heart", svg: <path d="M30 52 C 8 34, 8 12, 22 12 C 27 12, 30 16, 30 20 C 30 16, 33 12, 38 12 C 52 12, 52 34, 30 52 Z" /> },
];

const COLORS = [
  { name: "Red", hex: "#EF476F" },
  { name: "Yellow", hex: "#FFD166" },
  { name: "Green", hex: "#06D6A0" },
  { name: "Blue", hex: "#4CC9F0" },
  { name: "Purple", hex: "#B892FF" },
];

function pickTarget() {
  const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];
  return { shape, color };
}

function speak(text) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "en-US";
  u.rate = 0.9;
  u.pitch = 1.1;
  window.speechSynthesis.speak(u);
}

export default function ShapeGame() {
  const [target, setTarget] = useState(pickTarget());
  const [score, setScore] = useState(0);
  const [wrong, setWrong] = useState(null);
  const [nonce, setNonce] = useState(0);

  const options = useMemo(() => {
    const opts = new Set();
    const key = (s, c) => `${s.name}-${c.name}`;
    opts.add(key(target.shape, target.color));
    while (opts.size < 6) {
      const s = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      const c = COLORS[Math.floor(Math.random() * COLORS.length)];
      opts.add(key(s, c));
    }
    return Array.from(opts).sort(() => Math.random() - 0.5).map((k) => {
      const [sn, cn] = k.split("-");
      return { shape: SHAPES.find((s) => s.name === sn), color: COLORS.find((c) => c.name === cn) };
    });
  }, [target, nonce]);

  const say = () => speak(`Find the ${target.color.name} ${target.shape.name}`);

  const pick = (opt) => {
    if (opt.shape.name === target.shape.name && opt.color.name === target.color.name) {
      setScore((s) => s + 1);
      speak("Great match!");
      setTimeout(() => { setTarget(pickTarget()); setNonce((n) => n + 1); }, 700);
    } else {
      setWrong(`${opt.shape.name}-${opt.color.name}`);
      speak("Try again");
      setTimeout(() => setWrong(null), 500);
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-6 py-10" data-testid="shape-game">
      <div className="flex items-center justify-between">
        <Link to="/games" className="inline-flex items-center gap-2 text-brand-ink/80 hover:text-brand-red font-bold">
          <ArrowLeft size={18} /> All games
        </Link>
        <div className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-1.5 font-bold shadow-brutal-sm" data-testid="shape-score">
          <Sparkles size={16} /> Score: {score}
        </div>
      </div>

      <h1 className="mt-4 font-display text-4xl md:text-5xl font-bold text-brand-ink">Shapes & Colors</h1>
      <p className="mt-2 text-brand-ink/80 text-lg">
        Find the <strong style={{ color: target.color.hex }}>{target.color.name}</strong> <strong>{target.shape.name}</strong>.
      </p>

      <div className="mt-4">
        <button
          onClick={say}
          className="rounded-full border-2 border-brand-ink bg-white px-4 py-2 text-sm font-bold hover:bg-brand-yellow"
          data-testid="shape-say"
        >
          🔊 Say it again
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="shape-options">
        {options.map((o, i) => {
          const key = `${o.shape.name}-${o.color.name}`;
          return (
            <button
              key={i}
              onClick={() => pick(o)}
              data-testid={`shape-option-${key}`}
              className={`rounded-3xl border-[3px] border-brand-ink bg-white p-6 shadow-brutal-sm lift-hover aspect-square flex items-center justify-center ${wrong === key ? "animate-wiggle" : ""}`}
            >
              <svg viewBox="0 0 60 60" width="100%" height="100%" fill={o.color.hex} stroke="#2B2D42" strokeWidth="3" strokeLinejoin="round">
                {o.shape.svg}
              </svg>
            </button>
          );
        })}
      </div>

      <button
        onClick={() => { setTarget(pickTarget()); setNonce((n) => n + 1); }}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-white border-2 border-brand-ink px-5 py-2 font-bold btn-brutal"
        data-testid="shape-new"
      >
        <RefreshCw size={16} /> New target
      </button>
    </main>
  );
}
